import { LayoutContext } from 'contexts/LayoutContext';
import { Pressable, Row, styled, SxProp, useDripsyTheme, View, Image } from 'dripsy';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FlatList, ListRenderItemInfo } from 'react-native';
import { NativeModalView } from '@/design-system/components/modal/nativeModal/NativeModalView';
import { useNativeModalView } from '@/design-system/components/modal/nativeModal/useNativeModalView';
import { UtxAppEmbed } from '@/design-system/components/utxAppEmbedCallback/utxAppEmbedCallback';
import { LinearGradient } from 'expo-linear-gradient';

import { CardAccountStackRoutes, CardAccountStackScreenProps } from '../../card.navigator';
import { UniIconButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, TextTitle, TextView } from '@/design-system/components/typography';
import { useListNative } from './hooks/useListNative.hook';
import { CardStatusSelectFilters } from '../../options';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInput } from '@/design-system/components/inputs';
import { Divider } from '@/design-system/components/Divider';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { CardItem } from '../../hooks/useCardAccounts.hook';

export const CardListNativeScreen: React.FC<CardAccountStackScreenProps<CardAccountStackRoutes.CardDetails>> = ({ navigation, route }) => {
  const { record } = route.params;
  const { t } = useTranslation('card-account');
  const { theme } = useDripsyTheme();
  const onGoBack = () => {
    navigation.goBack();
  };

  const { animatedStyle, handleBack } = useNativeModalView({});
  const Gradient = styled(LinearGradient, {})({});

  const { bottomSheetFilterProps, filters } = useListNative({
    status: CardStatusSelectFilters(t),
    defaultValues: { status: [] }
  });

  const [search, setSearch] = useState<string | undefined>(undefined);

  const cards = useMemo(() => {
    if (!filters.status || filters.status.length === 0) return record.cards;
    return record.cards.filter((card) => filters.status?.includes(card.productType));
  }, [filters]);

  return (
    <NativeModalView animatedStyle={animatedStyle} onBack={handleBack} showHeader={false}>
      <UtxAppEmbed>
        <View sx={styles.contentContainer}>
          <Gradient colors={['rgba(49, 75, 108, 0.8)', 'rgba(39, 70, 115, 0.8)', '#3C6291', '#1E304B']}>
            <Row sx={{ justifyContent: 'space-between', alignItems: 'center', px: '$4', mt: '$5', mb: '$4' }}>
              <UniIconButton
                icon="arrowLeft"
                options={{ iconStyles: { color: theme?.colors?.$white as string } }}
                onPress={onGoBack}></UniIconButton>
              <View sx={{ textAlign: 'center' }}>
                <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
                  {t('card-account::landing-page.title--cards-of-card-account', { accountname: record.label })}
                </TextTitle>
              </View>
              <Pressable
                onPress={() => {
                  navigation.navigate(CardAccountStackRoutes.ListBottomSheetFilter, bottomSheetFilterProps);
                }}>
                <Icon
                  name="activityHistoryFilter"
                  color={bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : '$white'}
                  themeColor={bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : '$white'}
                />
                {bottomSheetFilterProps.filteringNumber > 0 && <View sx={styles.productFilterIconNumber} />}
              </Pressable>
            </Row>
          </Gradient>
          <View sx={styles.card}>
            <View sx={styles.table}>
              <View sx={styles.operations}>
                <View sx={styles.filters}>
                  <UniInput
                    sx={styles.filter_search}
                    size="default"
                    prefixIcon="search"
                    placeholder={t('card-account::landing-page.text--search-by-card-name-number')}
                    onValueChange={(value) => setSearch(value)}
                  />
                </View>
              </View>
              <FlatList
                data={cards}
                renderItem={({ item }: ListRenderItemInfo<CardItem>) => {
                  return (
                    <Pressable
                      sx={{ px: '$4' }}
                      onPress={() => {
                        window.parent.postMessage({ origin: 'card-account', data: { id: record.id, card: item.id } }, '*');
                      }}>
                      <View sx={{ marginY: '$2', borderRadius: 10, boxShadow: '2px 4px 20px rgba(0, 0, 0, 0.07)' }}>
                        <View sx={{ margin: '$4', gap: '$4' }}>
                          <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$3' }}>
                            <Image sx={styles.imageSx} source={item.productImageUrl || require('assets/images/worldcard_blank.png')} />
                            <View sx={{ justifyContent: 'center', gap: '$1', flex: 1 }}>
                              <View
                                sx={{
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: '$1'
                                }}>
                                <TextBody semiBold>{item.embossedName}</TextBody>
                                <TextCaption
                                  sx={{
                                    backgroundColor: theme.colors?.$backgroundInputDisabled as string,
                                    color: theme.colors?.$navbarTextInactive as string,
                                    borderRadius: 100,
                                    paddingX: '$2',
                                    paddingY: '$1'
                                  }}>{`**** ${item.last4}`}</TextCaption>
                              </View>
                              <TextCaption sx={{ color: theme.colors?.$textSecondary as string }}>{item.productName}</TextCaption>
                            </View>
                          </View>
                          <Divider />
                          <View sx={{ gap: '$1' }}>
                            <TextCaption sx={{ color: theme.colors?.$textSecondary as string }}>
                              {t('card-account::landing-page.text--todays-spending')}
                            </TextCaption>
                            <TextTitle semiBold sx={{ color: theme.colors?.$textPrimary as string }}>
                              {formatAmountByDecimals({ value: item.cardSpendingToday, suffix: record.assetSymbol })}
                            </TextTitle>
                          </View>
                        </View>
                      </View>
                    </Pressable>
                  );
                }}
              />
            </View>
          </View>
        </View>
      </UtxAppEmbed>
    </NativeModalView>
  );
};

enum StyleName {
  contentContainer = 'contentContainer',
  productFilterNativeIconBtn = 'productFilterNativeIconBtn',
  productFilterIconNumber = 'productFilterIconNumber',
  card = 'card',
  table = 'table',
  operations = 'operations',
  filters = 'filters',
  filter_search = 'filter_search',
  imageSx = 'imageSx'
}

const styles: Record<StyleName, SxProp> = {
  contentContainer: {
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
        "overview assets"
        "transaction assets"
        "transaction assets"
        `,
    gridTemplateRows: 'repeat(3, auto)'
  },
  productFilterNativeIconBtn: {
    height: '$9',
    width: '$9',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '$borderButton',
    borderRadius: 5
  },
  productFilterIconNumber: {
    position: 'absolute',
    right: 0,
    width: '$2',
    height: '$2',
    borderWidth: 1,
    borderRadius: 100,
    borderColor: '$hyperlink',
    backgroundColor: '$hyperlink',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '$4',
    borderRadius: '$radius0',
    boxShadow: 'none',
    paddingY: '$5',
    backgroundColor: '$white'
  },
  table: {
    flexDirection: 'column',
    gap: '$4'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    px: '$4',
    zIndex: 1
  },
  filters: {
    flexDirection: 'row',
    gap: '$5',
    width: '100%'
  },
  filter_search: {
    width: '100%'
  },
  imageSx: { width: 40, height: 24 }
};
